import React from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import Flexbox from 'flexbox-react';
import RIEToggle from 'components/core/editable/RIEToggle';
import ProcessUtils from 'utils/ProcessUtils';
import Constants from 'utils/Constants';
import InfoIcon from 'components/core/InfoIcon';
import { colorBlack, colorLightOrange } from 'styles/base';

const messageBoxStyles = css`
    margin-bottom: 15px;
    .message-box {
        width:100%;
    }
`;


export default class TransformationParameter extends React.Component {
    static propTypes = {
        process: PropTypes.object.isRequired,
        processErrors: PropTypes.object,
        hasWritePermissions: PropTypes.bool,
        parameterNames: PropTypes.array.isRequired,
        coordinateSystemName: PropTypes.string,
        dimensionName: PropTypes.string,
        exploredDatastreams: PropTypes.object,
        isOverridable: PropTypes.bool,
        isDefaultValueVisibleInitially: PropTypes.bool,
        isDatastreamDefaultVisibileInitially: PropTypes.bool,
        isOverrideVisibleInitially: PropTypes.bool,
        overrideQuestionText: PropTypes.string,
        infoBoxText: PropTypes.any,
        displayInputDatastream: PropTypes.bool,
        className: PropTypes.string,

        // So you can override flex property of the wrapper flexbox container
        // I had to add this because TransformationTypeParameter wasn't flexing even though all the other ones were
        flex: PropTypes.string
    };

    static defaultProps = {
        infoBoxText: ''
    };

    constructor(props) {
        super(props);
        this.titleFont = css`font-weight:900; line-height: 15px;`;
        this.divIndent = '30px';
    }

    isOverrideVisible = () => {
        //can just use first index because in cases where two parameters if one exists they both exist
        return ProcessUtils.shouldDisplayTransformationOverride(this.props.process, this.props.parameterNames, this.props.dimensionName);
    };

    isDatastreamDefaultVisible = () => {
        return ProcessUtils.shouldDisplayDatastreamDefault(this.props.process, this.props.parameterNames);
    };

    isDefaultValueVisible = () => {
        //can just use first index because in cases where two parameters if one exists they both exist
        return ProcessUtils.hasTransformationDefault(this.props.process, this.props.parameterNames, this.props.coordinateSystemName, this.props.dimensionName);
    };

    shouldDatastreamDefaultSectionBeDisplayed = () => {
        return ((this.props.isOverridable && this.isDatastreamDefaultVisible()) || this.props.isDatastreamDefaultVisibileInitially);
    };

    shouldOverrideSectionBeDisplayed = () => {
        return ((this.props.isOverridable && this.isDefaultValueVisible()) || this.props.isOverrideVisibleInitially);
    };

    getCoordinateSystemDefaultValue = (parameterName = null) => {
        let value = null;
        parameterName = parameterName ? parameterName : this.props.parameterNames[0];
        ProcessUtils.visitTransformationCoordinateSystemDefaults(this.props.process, parameterName, (coordinateObj) => {
            if (coordinateObj.name === this.props.coordinateSystemName && coordinateObj.dim === this.props.dimensionName) {
                value = coordinateObj.default_value;
            }
        });

        return value;
    };

    getCoordinateSystemDefaultFieldPath = () => {
        return ['variable_retrieval', 'transformation_parameters', this.props.parameterNames[0], this.props.coordinateSystemName, this.props.dimensionName, 'default_value'];
    };

    onDefaultValueChange = (fieldPath, value, data) => {
        const parameterName = data && data.parameterName ? data.parameterName : this.props.parameterNames[0];

        if (parameterName === 'range' && value === 0) {
            // If value is 0, then change that to the default value of LENGTH_OF_PROCESSING_INTERVAL
            value = Constants.RANGE_DEFAULT;
        }
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.updateTransformationParameterDefault(parameterName, this.props.coordinateSystemName, this.props.dimensionName, value);
        editor.commit();
    };

    onDefaultDisplayChange = () => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const parameterNames = this.props.parameterNames;
        const coordinateSystemName = this.props.coordinateSystemName;
        const dimensionName = this.props.dimensionName;

        if (ProcessUtils.hasTransformationDefault(this.props.process, parameterNames, coordinateSystemName, dimensionName)) {
            editor.removeTransformationParameterDefault(parameterNames, coordinateSystemName, dimensionName);
        } else {
            editor.addTransformationDefault(parameterNames, coordinateSystemName, dimensionName);
        }
        editor.commit();
    };

    onDatastreamDefaultDisplayChange = () => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const parameterNames = this.props.parameterNames;

        if (ProcessUtils.shouldDisplayDatastreamDefault(editor.process, parameterNames)) {
            editor.removeTransformationParameterDatastreamDefault(parameterNames);
        } else {
            editor.addTransformationParameterDatastreamDefault(parameterNames);
        }
        editor.commit();
    };

    onOverrideDisplayChange = () => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const parameterNames = this.props.parameterNames;
        const coordinateSystemName = this.props.coordinateSystemName;
        const dimensionName = this.props.dimensionName;

        if (ProcessUtils.shouldDisplayTransformationOverride(editor.process, parameterNames, dimensionName)) {
            editor.removeTransformationParameterOverride(parameterNames, coordinateSystemName, dimensionName);
        } else {
            editor.addTransformationParameterOverride(parameterNames, coordinateSystemName, dimensionName, this.props.exploredDatastreams);
        }
        editor.commit();
    };

    getMessageBoxVariableList = (preListText = null) => {
        const variables = ProcessUtils.getVariablesWithRelevantExploredData(this.props.process, this.props.parameterNames[0], this.props.exploredDatastreams);
        const list = [];

        Object.keys(variables).forEach(datastreamName => {
            const retrievedVariables = variables[datastreamName];
            Object.keys(retrievedVariables).forEach(variableName => {
                list.push(
                    <li key={`${datastreamName}_${variableName}`}>
                        {datastreamName}: <span className={css`color:${colorBlack}`}>{variableName}</span>
                    </li>
                );
            });
        });

        return (
            <Flexbox flexDirection={'column'}>
                {list.length > 0 &&
                <p>{preListText}</p>
                }
                <ul>
                    {list}
                </ul>
            </Flexbox>
        );
    };

    shouldParameterBeDisplayed() {
        let shouldShow = true;

        this.props.parameterNames.forEach(name => {
            let isRelevant = false;
            if (name === 'qc_mask' || name === 'qc_bad' || name === 'missing_value') {
                // if this is an input parameter, it does not depend upon dimensions
                isRelevant = ProcessUtils.isRelevantInputDatastreamParameter(this.props.process, name, this.props.exploredDatastreams);

            } else {
                isRelevant = ProcessUtils.isRelevantCoordinateSystemParameter(this.props.process, name, [this.props.coordinateSystemName], this.props.dimensionName, this.props.exploredDatastreams);
            }

            const hasTransformationParameter = ProcessUtils.hasTransformationParameter(this.props.process, name);
            shouldShow &= (hasTransformationParameter && isRelevant);
        });
        return shouldShow;
    }

    renderMessageBox = () => {
        // child overrides to include message box
        return null;
    };

    renderQuestionText = () => {
        // child overrides to handle question text
        return null;
    };

    renderQuestion = () => {
        if (this.renderQuestionText()) {
            return (
                <Flexbox flexDirection={'row'} alignItems="center" marginBottom="15px">
                    {this.props.infoBoxText &&
                    <InfoIcon
                        message={this.props.infoBoxText}
                    />
                    }
                    {this.renderQuestionText() &&
                    <div className={this.titleFont}>
                        {this.renderQuestionText()}
                    </div>
                    }
                    {!this.props.isDefaultValueVisibleInitially &&
                    <Flexbox>
                        <RIEToggle
                            value={this.isDefaultValueVisible()}
                            textFalse="No"
                            textTrue="Yes"
                            change={this.onDefaultDisplayChange}
                            radioGroupName={`${this.props.parameterNames[0]}-default`}
                            formDisplay
                            isDisabled={!this.props.hasWritePermissions}
                            processId={this.props.process.name}
                            toggleNoWarning
                        />
                    </Flexbox>
                    }
                </Flexbox>
            );
        }

        return null;
    };

    renderDefaultComponent = () => {
        // Each child class will override to implement their custom section
        return null;
    };

    renderDatastreamDefaultQuestionText = () => {
        return 'Do you want to override this value by datastream?';
    };

    renderOverrideQuestionText = () => {
        return 'Do you want to override this value by variable?';
    };

    renderOverride = () => {
        const bothDefaultsAndOverrides = this.props.isDefaultValueVisibleInitially && this.props.isDatastreamDefaultVisibileInitially && this.props.isOverrideVisibleInitially;
        const indent = this.props.isOverrideVisibleInitially && !bothDefaultsAndOverrides ? '0' : this.divIndent;
        const overrideSectionStyles = css`
            margin: 15px ${this.divIndent} 0 ${indent};
            .table {
                margin-bottom:10px;
            }
            .fa-question-circle {
                color: ${colorLightOrange};
                font-size: 20px;
        `;

        const questionStyles = this.props.isOverrideVisibleInitially ? this.titleFont : '';

        const overrideIndent = this.props.isOverrideVisibleInitially && !bothDefaultsAndOverrides ? this.divIndent : '0';
        const overrideComponentStyles = css`
            width: 100%;
            padding-left: ${overrideIndent};
        `;
        const spacer = <div className="last-row-spacer"/>;
        const overrideTable = this.isOverrideVisible() ? this.renderOverrideTable() : spacer;
        const datastreamDefaultTable = this.isDatastreamDefaultVisible() ? this.renderDatastreamDefaultTable() : spacer;

        let datastreamDefaultComponent = null;
        let overrideComponent = null;

        if (this.shouldDatastreamDefaultSectionBeDisplayed()) {
            datastreamDefaultComponent = (
                <Flexbox flexDirection={'column'} className={cx('override-section', overrideSectionStyles)}>
                    <Flexbox flexDirection={'row'} alignItems="center">
                        <div className={`question-text ${questionStyles}`}>{this.renderDatastreamDefaultQuestionText()}</div>
                        <Flexbox>
                            <RIEToggle
                                value={this.isDatastreamDefaultVisible()}
                                textFalse="No"
                                textTrue="Yes"
                                change={this.onDatastreamDefaultDisplayChange}
                                radioGroupName={`${this.props.parameterNames[0]}-ds-override`}
                                formDisplay
                                isDisabled={!this.props.hasWritePermissions}
                                processId={this.props.process.name}
                                toggleNoWarning
                            />
                        </Flexbox>
                    </Flexbox>
                    <Flexbox className={overrideComponentStyles}>
                        {datastreamDefaultTable}
                    </Flexbox>
                </Flexbox>
            );
        }

        if (this.shouldOverrideSectionBeDisplayed()) {
            overrideComponent = (
                <Flexbox flexDirection={'column'} className={cx('override-section', overrideSectionStyles)}>
                    <Flexbox flexDirection={'row'} alignItems="center">
                        <div className={`question-text ${questionStyles}`}>{this.renderOverrideQuestionText()}</div>
                        <Flexbox>
                            <RIEToggle
                                value={this.isOverrideVisible()}
                                textFalse="No"
                                textTrue="Yes"
                                change={this.onOverrideDisplayChange}
                                radioGroupName={`${this.props.parameterNames[0]}-override`}
                                formDisplay
                                isDisabled={!this.props.hasWritePermissions}
                                processId={this.props.process.name}
                                toggleNoWarning
                            />
                        </Flexbox>
                    </Flexbox>
                    <Flexbox className={overrideComponentStyles}>
                        {overrideTable}
                    </Flexbox>
                </Flexbox>
            );
        }

        return (
            <React.Fragment>
                {datastreamDefaultComponent}
                {overrideComponent}
            </React.Fragment>
        );
    };

    renderDatastreamDefaultTable = () => {
        // child class will override to implement their version of the default table
        return <div/>;
    };

    renderOverrideTable = () => {
        // child class will override to implement their version of the override table
        return <div/>;
    };

    render = () => {
        let flexProp = '';
        if (this.props.flex) {
            flexProp = `flex: ${this.props.flex}`;
        }
        const wrapperStyle = css`
            margin-bottom:15px;
            ${flexProp}
        `;

        const defaultStyle = css`padding-left: ${this.divIndent};`;

        if (this.shouldParameterBeDisplayed()) {
            return (
                <Flexbox flexDirection={'column'} className={cx('transform-parameter', wrapperStyle, this.props.className)}>
                    {this.renderMessageBox() ?
                        <Flexbox flexDirection={'row'} flexGrow={1} className={messageBoxStyles}>
                            {this.renderMessageBox()}
                        </Flexbox>
                        :
                        null
                    }
                    {this.renderQuestion()}
                    {this.isDefaultValueVisible() &&
                    <Flexbox className={defaultStyle}>
                        {this.renderDefaultComponent()}
                    </Flexbox>
                    }
                    {this.renderOverride()}
                </Flexbox>
            );
        } else {
            return <div/>;
        }

    };
}



// WEBPACK FOOTER //
// ./src/components/core/fields/transformation_parameters/TransformationParameter.js